import { makeAutoObservable } from 'mobx'
import {
  type IResponseMediaShort,
  extractLinksFromText,
  resolvePreviewAttachments,
} from 'entities/Attachment'
import { Attachment } from 'entities/Attachment/model/Attachment'
import { type IResponseBroadcastById } from 'entities/Broadcast/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import {
  type ISMSMessageFieldStoreProps,
  SMSMessageFieldStore,
} from 'widgets/MessageFormFields/SMSMessageField/store/SMSMessageFieldStore'

export class BroadcastSMSStore {
  smsMessageFieldStore: SMSMessageFieldStore
  constructor(props: ISMSMessageFieldStoreProps, private _entities: IEntitiesStore) {
    this.smsMessageFieldStore = new SMSMessageFieldStore(props, this._entities)
    makeAutoObservable(this)
  }

  init = async (response: IResponseBroadcastById) => {
    const messageText = response.message || ''

    const allUrlsInMessage = extractLinksFromText(messageText)

    const mediaUrlShortUrls = this.getMediaUrlShortUrls(response.media_url || [])

    // URLs in the message body that are not in media_url — potential non-media
    // attachments (documents / audio) or regular links.
    const urlsToCheckForAttachments = allUrlsInMessage.filter((url) => !mediaUrlShortUrls.has(url))

    // Create attachments from media_url (images, videos, documents, audio)
    const mediaAttachments = this.createAttachmentsFromMediaUrl(response.media_url || [])

    // Resolve non-media attachments referenced by body links via the attachment store.
    const bodyAttachments = await resolvePreviewAttachments(
      this._entities.attachmentStore,
      urlsToCheckForAttachments
    )

    const allAttachments = [...mediaAttachments, ...bodyAttachments]

    const updatedMessageText = this.replaceOutdatedUrls(messageText, allUrlsInMessage, response)

    this.messageFieldStore.setMessageData({
      attachments: allAttachments,
      message: updatedMessageText,
      isFocus: false,
      isReset: false,
      isReplace: true,
    })
  }

  private getMediaUrlShortUrls(mediaUrl: IResponseMediaShort[]): Set<string> {
    return new Set(
      mediaUrl.map((media) => media.source_short).filter((url): url is string => Boolean(url))
    )
  }

  private createAttachmentsFromMediaUrl(mediaUrl: IResponseMediaShort[]): Attachment[] {
    if (!mediaUrl.length) return []

    const usedShortUrls = new Set<string>()

    return mediaUrl.map((media) => {
      // Check if this is a GIF preview for a video
      const isPreviewGif = !!(
        media.content_type === 'image/gif' &&
        media.source_short &&
        media.original_source &&
        media.original_source !== media.source
      )

      const attachment = new Attachment({
        responseMediaShort: {
          ...media,
          is_allowed_for_media_url: isPreviewGif ? true : media.is_allowed_for_media_url,
        },
      })

      if (media.source_short && !usedShortUrls.has(media.source_short)) {
        attachment.source_short = media.source_short
        usedShortUrls.add(media.source_short)
      }

      return attachment
    })
  }

  private replaceOutdatedUrls(
    messageText: string,
    allUrlsInMessage: string[],
    response: IResponseBroadcastById
  ): string {
    if (!response.media_url || allUrlsInMessage.length === 0) {
      return messageText
    }

    const responseShortUrls =
      response.short_urls
        ?.map((item) => item.short_url)
        .filter((url): url is string => Boolean(url)) || []

    const mediaUrlShortUrls =
      response.media_url
        ?.map((media) => media.source_short)
        .filter((url): url is string => Boolean(url)) || []

    const allKnownShortUrls = [...new Set([...responseShortUrls, ...mediaUrlShortUrls])]

    const shortenDomains = this._entities.shortLinkStore.shortenDomains || []
    const isShortUrl = (url: string): boolean => {
      try {
        const urlObj = new URL(url)
        return shortenDomains.some((domain) => urlObj.hostname.includes(domain))
      } catch {
        return false
      }
    }

    const unmatchedUrlsInMessage = allUrlsInMessage.filter(
      (url) => !allKnownShortUrls.includes(url) && isShortUrl(url)
    )

    const unmatchedUrlsInPayload = responseShortUrls.filter(
      (url) => !allUrlsInMessage.includes(url)
    )

    if (unmatchedUrlsInMessage.length > 0 && unmatchedUrlsInPayload.length > 0) {
      const urlUpdateMap = new Map<string, string>()

      if (unmatchedUrlsInMessage.length === unmatchedUrlsInPayload.length) {
        unmatchedUrlsInMessage.forEach((oldUrl, index) => {
          if (index < unmatchedUrlsInPayload.length) {
            urlUpdateMap.set(oldUrl, unmatchedUrlsInPayload[index])
          }
        })
      } else if (unmatchedUrlsInPayload.length >= unmatchedUrlsInMessage.length) {
        unmatchedUrlsInMessage.forEach((oldUrl, index) => {
          if (index < unmatchedUrlsInPayload.length) {
            urlUpdateMap.set(oldUrl, unmatchedUrlsInPayload[index])
          }
        })
      }

      if (urlUpdateMap.size > 0) {
        let updatedMessageText = messageText
        urlUpdateMap.forEach((newUrl, oldUrl) => {
          updatedMessageText = updatedMessageText.replace(oldUrl, newUrl)
        })
        return updatedMessageText
      }
    }

    return messageText
  }

  get messageDataParams() {
    return this.smsMessageFieldStore.messageDataParams
  }

  get messageFieldStore() {
    return this.smsMessageFieldStore.messageFieldStore
  }

  get isError() {
    return this.smsMessageFieldStore.isError
  }

  get segments_count_rcs() {
    return this.smsMessageFieldStore.segments_count_rcs
  }

  get segments_count() {
    return this.smsMessageFieldStore.segments_count
  }
}
